import { ClippingStudioSubtitleEvent } from "app/js/src/domain/clipping-studio/clipping-studio-project";
import { Subtitle } from "app/js/src/domain/subtitle";

/** Raw cue shape from media subtitle JSON download. */
export interface MediaSubtitleCueJson {
  StartTimeMS?: number;
  EndTimeMS?: number;
  Text?: string;
  Commands?: string;
  HasMoreParts?: boolean;
}

export interface MediaSubtitleFileJson {
  Language?: string;
  Subs?: MediaSubtitleCueJson[];
}

/** Normalize language map keys (consistent casing). */
export function normalizeSubtitleLanguageKey(language: string | undefined | null): string {
  return (language ?? "").trim().toLowerCase();
}

/** Synthetic track keys for file import: `srt-1`, `vtt-2`, … */
const IMPORTED_SUBTITLE_TRACK_KEY = /^(srt|vtt)-(\d+)$/;

export function isImportedSubtitleTrackKey(
  language: string | undefined | null,
): boolean {
  return IMPORTED_SUBTITLE_TRACK_KEY.test(
    normalizeSubtitleLanguageKey(language),
  );
}

/** Next unused `{format}-{n}` key among existing map keys. */
export function allocateImportedSubtitleTrackKey(
  format: "srt" | "vtt",
  existingKeys: Iterable<string>,
): string {
  const prefix = `${format}-`;
  let max = 0;
  for (const raw of existingKeys) {
    const key = normalizeSubtitleLanguageKey(raw);
    if (!key.startsWith(prefix)) continue;
    const n = parseInt(key.slice(prefix.length), 10);
    if (Number.isFinite(n) && n > max) max = n;
  }
  return `${prefix}${max + 1}`;
}

/** UI title for imported tracks (`SRT #1`); null for media language keys. */
export function importedSubtitleTrackDisplayName(
  language: string | undefined | null,
): string | null {
  const match = normalizeSubtitleLanguageKey(language).match(
    IMPORTED_SUBTITLE_TRACK_KEY,
  );
  if (!match) return null;
  return `${match[1].toUpperCase()} #${match[2]}`;
}

/**
 * Flatten multipart cues (HasMoreParts) — same idea as videoContainer._handleMultipartsSubs.
 */
export function flattenMultipartSubtitleSubs(
  subs: MediaSubtitleCueJson[],
): MediaSubtitleCueJson[] {
  const updated: MediaSubtitleCueJson[] = [];
  let prev: MediaSubtitleCueJson | undefined;
  for (const current of subs) {
    if (current.HasMoreParts) {
      prev = current;
      continue;
    }
    if (
      prev &&
      prev.StartTimeMS === current.StartTimeMS &&
      prev.EndTimeMS === current.EndTimeMS
    ) {
      current.Text = `${prev.Text ?? ""}\r\n${current.Text ?? ""}`;
    }
    updated.push(current);
    prev = current;
  }
  return updated;
}

/** Stable studio id for merge / selection across reloads. */
export function buildMediaSubtitleCueId(
  mediaSubtitleRowId: number,
  cueIndex: number,
): string {
  return `sub-${mediaSubtitleRowId}-${cueIndex}`;
}

/** Media-backed cue ids are `sub-{rowId}-{cueIndex}` (two numeric segments). */
export function isMediaBackedSubtitleCueId(id: string): boolean {
  return /^sub-\d+-\d+$/.test(id);
}

/**
 * Drop media-backed edits whose original cue id no longer exists.
 * Keep user-added cues (non media-backed ids).
 */
export function pruneOrphanSubtitleEdits(
  originalsByLanguage: Record<string, ClippingStudioSubtitleEvent[]>,
  editsByLanguage: Record<string, ClippingStudioSubtitleEvent[]>,
): Record<string, ClippingStudioSubtitleEvent[]> {
  const result: Record<string, ClippingStudioSubtitleEvent[]> = {};
  for (const [lang, edits] of Object.entries(editsByLanguage ?? {})) {
    const key = normalizeSubtitleLanguageKey(lang);
    if (!key) continue;
    const originalIds = new Set(
      (originalsByLanguage[key] ?? []).map((o) => o.id),
    );
    const kept = (edits ?? []).filter((e) => {
      if (!isMediaBackedSubtitleCueId(e.id)) return true;
      return originalIds.has(e.id);
    });
    if (kept.length > 0) {
      result[key] = kept;
    }
  }
  return result;
}

/**
 * Convert downloaded media subtitle JSON into studio cues for one track.
 * One language → one track: caller must dedupe tracks before calling.
 */
export function mediaSubtitleJsonToStudioCues(
  mediaSubtitleRowId: number,
  language: string,
  json: MediaSubtitleFileJson | null | undefined,
): ClippingStudioSubtitleEvent[] {
  const rawSubs = Array.isArray(json?.Subs) ? json.Subs : [];
  const flat = flattenMultipartSubtitleSubs(rawSubs);
  const lang = normalizeSubtitleLanguageKey(language);
  return flat.map((cue, cueIndex) => ({
    id: buildMediaSubtitleCueId(mediaSubtitleRowId, cueIndex),
    StartTimeMS: Number(cue.StartTimeMS) || 0,
    EndTimeMS: Number(cue.EndTimeMS) || 0,
    Text: cue.Text ?? "",
    ...(cue.Commands != null && cue.Commands !== ""
      ? { Commands: cue.Commands }
      : {}),
    language: lang,
    fromMainSource: true,
  }));
}

/**
 * Keep one media subtitle track per language (first by ascending track id).
 * Regular subtitles only — caller should pass MediaInfo.subtitles, not closedCaptions.
 */
export function pickOneSubtitleTrackPerLanguage(
  tracks: Subtitle[],
): Subtitle[] {
  const byLang = new Map<string, Subtitle>();
  const sorted = [...tracks].sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
  for (const track of sorted) {
    const key = normalizeSubtitleLanguageKey(track.language);
    if (!key || byLang.has(key)) continue;
    byLang.set(key, track);
  }
  return [...byLang.values()];
}
